import { ComparisonType } from './comparison-type.enum';
import { KeyIndicatorType } from '../../user-preferences/key-indicator-type.enum';
import { SalesSqftDisplayType } from '../../user-preferences/sales-sqft-display-type.enum';
import { SalesVolumeDisplayType } from '../../user-preferences/sales-volume-display-type.enum';
import { FitType } from '../fit/fit-type.enum';
import { FeatureCollection } from '../../models';

/**
 * Request payload for Store Ranking API
 *
 * API Endpoint: POST /api/insights/store/<STORE_UUID>/ranking
 *
 * Task in progress – verify payload mapping with existing filters
 *
 * Required Headers (handled by AuthorizationHeaderInterceptor):
 *   - Authorization: Bearer <ACCESS_TOKEN>
 *   - Content-Type: application/json
 *
 * Payload Structure (verified):
 * {
 *   "geoJson": { <FeatureCollection> },          // Geographic boundary for ranking comparison
 *   "comparisonType": "ALL|BANNER|FIT_CATEGORY", // Store comparison filter
 *   "keyIndicator": "SALES_VOLUME|SALES_SQFT|STORE_COUNT", // Metric to rank by
 *   "salesSqftDisplayType": "TOTAL_AREA|SALES_AREA", // Square footage calculation type
 *   "salesVolumeDisplayType": "ANNUAL|WEEKLY",   // Sales volume time period
 *   "bannerUuid": "string",                      // Required when comparisonType is BANNER
 *   "fitCategory": "string"                      // Required when comparisonType is FIT_CATEGORY
 * }
 *
 * Example Payload (comparisonType: BANNER):
 * {
 *   "bannerUuid": "9db1b7c0-f11d-11ea-b63d-0e17c0c0bb34",
 *   "comparisonType": "BANNER",
 *   "geoJson": { ... },
 *   "keyIndicator": "SALES_VOLUME",
 *   "salesSqftDisplayType": "TOTAL_AREA",
 *   "salesVolumeDisplayType": "WEEKLY"
 * }
 */
export class StoreRankingRequest {
  /** Banner UUID - required when comparisonType is BANNER (e.g., "9db1b7c0-f11d-11ea-b63d-0e17c0c0bb34") */
  bannerUuid: string;

  /** Comparison type filter: ALL (all grocery stores), BANNER (same banner), FIT_CATEGORY (same fit category) */
  comparisonType: ComparisonType;

  /** Fit category - required when comparisonType is FIT_CATEGORY (e.g., "CONVENTIONAL") */
  fitCategory: FitType;

  /** GeoJSON FeatureCollection defining the geographic boundary for ranking comparison */
  geoJson: FeatureCollection;

  /** Key indicator to rank stores by: SALES_VOLUME, SALES_SQFT, or STORE_COUNT */
  keyIndicator: KeyIndicatorType;

  /** Sales per square foot display type: TOTAL_AREA or SALES_AREA */
  salesSqftDisplayType: SalesSqftDisplayType;

  /** Sales volume display type: ANNUAL or WEEKLY */
  salesVolumeDisplayType: SalesVolumeDisplayType;

  /** FIPS code for the store location */
  fips: string;

  constructor(raw?: any) {
    if (raw) {
      Object.assign(this, raw);

      if (raw.comparisonType) {
        // @ts-ignore
        this.comparisonType = ComparisonType[<string>raw.comparisonType];
      }
      if (raw.fitCategory) {
        // @ts-ignore
        this.fitCategory = FitType[<string>raw.fitCategory];
      }
      if (raw.geoJson) {
        this.geoJson = new FeatureCollection(raw.geoJson);
      }
      if (raw.keyIndicator) {
        // @ts-ignore
        this.keyIndicator = KeyIndicatorType[<string>raw.keyIndicator];
      }
      if (raw.salesSqftDisplayType) {
        // @ts-ignore
        this.salesSqftDisplayType = SalesSqftDisplayType[<string>raw.salesSqftDisplayType];
      }
      if (raw.salesVolumeDisplayType) {
        // @ts-ignore
        this.salesVolumeDisplayType = SalesVolumeDisplayType[<string>raw.salesVolumeDisplayType];
      }
    }
  }

  /**
   * Converts the request to a plain JSON object suitable for API submission.
   * Excludes fips field as requested.
   */
  toJSON(): object {
    const payload: any = {
      geoJson: this.geoJson,
      comparisonType: this.comparisonType,
      keyIndicator: this.keyIndicator,
      salesSqftDisplayType: this.salesSqftDisplayType,
      salesVolumeDisplayType: this.salesVolumeDisplayType
    };

    // Only include optional fields if they have values
    if (this.bannerUuid) {
      payload.bannerUuid = this.bannerUuid;
    }
    if (this.fitCategory) {
      payload.fitCategory = this.fitCategory;
    }
    // Explicitly exclude fips field
    if(this.fips){
      payload.fips = this.fips;
    }

    return payload;
  }
}
